import React from 'react';
import type { ElementNodeDto } from '@xshop/shared';
import { isBuilderPage } from '../render/ElementPlaceholder';
import { textProp, urlProp } from '../render/elementProps';
import { useElementBox, type ElementComponent } from '../render/elementRenderContext';
import { FormScope, useEnclosingForm, useFormAnswers, type FieldValue, type FormAnswers } from './formScope';
import { sendFormSubmission } from './formSubmission';
import { validateFields } from './formValidation';
import './forms.css';

/**
 * The box that gathers the fields inside it.
 *
 * It owns one set of answers, puts them in scope, and every `input`, `select`, `textarea`, `checkbox`
 * and `file-upload` below it reads and writes that one set — which is what makes a form out of a pile
 * of independent nodes. Pressing a `button` whose kind is `submit` checks every field against the rules
 * its own merchant set, and shows each message under the field that earned it.
 *
 * A checked submission is stored by the platform's own endpoint (`formSubmission.ts`), announced on
 * the page as `xshop:form-submit` for whatever the merchant installed, and then the visitor is thanked
 * or sent where the merchant said. The builder never submits: a merchant clicking their own submit
 * button is selecting it, not filling it in.
 */

/** The event a stored submission is announced under, for whatever the merchant has installed on the page. */
export const FORM_SUBMIT_EVENT = 'xshop:form-submit';

/** What that event carries: which form it was, and the answers it collected under their field names. */
export type FormSubmitDetail = { form: string; values: Readonly<Record<string, FieldValue>> };

// Announces a stored submission on the page, so nothing a merchant installed has to poll for one.
function announce(detail: FormSubmitDetail): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent<FormSubmitDetail>(FORM_SUBMIT_EVENT, { detail }));
}

// Draws the one line a form says about itself: what was wrong, or that it is done.
const Banner: React.FC<{ tone: 'bad' | 'good'; text: string }> = ({ tone, text }) =>
  text === '' ? null : <span className="xs-form-banner" data-tone={tone} role="status" dir="auto">{text}</span>;

// Puts the keyboard in the first field that was refused, so a long form never has to be re-read to find it.
function focusRefused(answers: FormAnswers, found: Readonly<Record<string, string>>): void {
  const first = answers.rules.find((rule) => found[rule.name]);
  if (first && typeof document !== 'undefined') document.getElementById(first.domId)?.focus();
}

// Checks every field against its merchant's rules and answers whether the answers may be sent.
function checkAnswers(answers: FormAnswers): boolean {
  const found = validateFields(answers.rules, answers.values);
  answers.setErrors(found);
  if (Object.keys(found).length > 0) {
    // Each refused field says why under itself; a banner over the form as well said it twice.
    answers.setStatus('invalid');
    focusRefused(answers, found);
    return false;
  }
  return true;
}

// Stores the checked answers server-side, announces them, and answers whether the visitor may be thanked.
async function deliverAnswers(node: ElementNodeDto, answers: FormAnswers): Promise<boolean> {
  const name = textProp(node.props, 'name', node.id);
  answers.setStatus('sending');
  try {
    await sendFormSubmission(node.id, name, answers.values);
  } catch {
    answers.setStatus('failed');
    return false;
  }
  answers.setStatus('sent');
  announce({ form: name, values: answers.values });
  return true;
}

// Draws a form a merchant dropped inside another one as a plain box: one page, one submission.
const NestedForm: ElementComponent = ({ node, children }) => <div {...useElementBox(node)}>{children}</div>;

// Draws the form: its fields while it is being filled in, and the merchant's thank-you once it is not.
const OwnForm: ElementComponent = ({ node, children }) => {
  const answers = useFormAnswers();
  const box = useElementBox(node);
  const next = urlProp(node.props, 'href');
  const thanks = textProp(node.props, 'successMessage');

  // 1. Checks, stores, then thanks or redirects; the builder and a submission already in flight do neither.
  const onSubmit = (event: React.FormEvent): void => {
    event.preventDefault();
    if (isBuilderPage() || answers.status === 'sending' || !checkAnswers(answers)) return;
    void deliverAnswers(node, answers).then((stored) => {
      if (stored && next) window.location.assign(next);
    });
  };

  // 2. The fields stay up until the answers are stored; only then does the thank-you replace them.
  const done = answers.status === 'sent' && !next;
  return (
    <FormScope answers={answers}>
      <form {...box} onSubmit={onSubmit} noValidate aria-label={textProp(node.props, 'name') || undefined}>
        {answers.status === 'failed' ? <Banner tone="bad" text={textProp(node.props, 'errorMessage')} /> : null}
        {done ? <Banner tone="good" text={thanks} /> : children}
      </form>
    </FormScope>
  );
};

/**
 * Draws the box that gathers fields. A `form` a merchant dropped inside another one draws as a plain
 * box instead: a nested `<form>` is markup no browser keeps, and the inner one would collect answers
 * that never reach a submission — so the outer form owns every field on the page it holds.
 */
export const FormElement: ElementComponent = ({ node, children }) => {
  const Draw = useEnclosingForm() ? NestedForm : OwnForm;
  return <Draw node={node}>{children}</Draw>;
};
